import type { ElementType } from 'react';
import { Mail } from '@/assets/icons/interview-plan';
import { getActivityKindIcon } from '@/components/automation/MoveWithAutomationModal/activityKindIcon';
import type { UnderwayActivity } from './types';

// 3rd-party assessment kinds — these render a "using <provider> [icon]" clause
// instead of going straight to the candidate. Mirrors the FE automatable set.
export const EXTERNAL_ASSESSMENT_KINDS = [
  'hackerrank',
  'hackerearth_assessment',
  'criteriacorp_assessment',
  'codeinterview_assessment',
  'hireflix_assessment',
] as const;

const PROVIDER_NAME_BY_KIND: Record<string, string> = {
  hackerrank: 'HackerRank',
  hackerearth_assessment: 'HackerEarth',
  criteriacorp_assessment: 'Criteria',
  codeinterview_assessment: 'CodeInterview',
  hireflix_assessment: 'Hireflix',
};

const isExternalAssessment = (kind: string): boolean =>
  (EXTERNAL_ASSESSMENT_KINDS as readonly string[]).includes(kind);

export const getProviderName = (kind: string): string =>
  PROVIDER_NAME_BY_KIND[kind] ?? '3rd party';

// Interview scheduling method (BE `automation_mode`) → short display label.
const SCHEDULE_MODE_LABELS: Record<string, string> = {
  self_schedule: 'self-schedule link',
  request_availability: 'availability request',
  ai_coordinator: 'Kula Coordinator',
};

export const getScheduleModeLabel = (mode: string | undefined): string =>
  (mode && SCHEDULE_MODE_LABELS[mode]) || '';

export type ActivitySubjectPreposition = 'to' | 'for' | 'on';

// The optional clause between the candidate and the "in the <stage>" tail.
// A kind has at most one: reviewer/user recipients (review, HM survey) OR a
// "using <X>" phrase (interview scheduling mode, 3rd-party provider).
export type ActivityExtraClause =
  | { type: 'recipient'; noun: 'reviewer' | 'user' }
  | { type: 'using'; label: string; icon: ElementType }
  | null;

export type ActivityCopyConfig = {
  verb: 'Sending' | 'Scheduling';
  subjectPreposition: ActivitySubjectPreposition;
  extra: ActivityExtraClause;
};

// The per-kind sentence shape for the underway row (see the design spec):
//   email / assessment / candidate-survey → "Sending X to <cand> …"
//   interview                             → "Scheduling X for <cand> using ✉<mode> …"
//   review                                → "Sending X on <cand> to 👤<reviewer> …"
//   HM (employee) survey                  → "Sending X on <cand> to 👤<user> …"
//   3rd-party assessment                  → "Sending X to <cand> using [icon]<provider> …"
export const getActivityCopyConfig = (
  activity: Pick<UnderwayActivity, 'activityKind' | 'details'>
): ActivityCopyConfig => {
  const kind = activity.activityKind;

  if (kind === 'interview') {
    const label = getScheduleModeLabel(activity.details?.scheduleMode);
    return {
      verb: 'Scheduling',
      subjectPreposition: 'for',
      // Skip the clause when the mode is unknown rather than print "using".
      extra: label ? { type: 'using', label, icon: Mail } : null,
    };
  }

  if (kind === 'review') {
    return {
      verb: 'Sending',
      subjectPreposition: 'on',
      extra: { type: 'recipient', noun: 'reviewer' },
    };
  }

  if (kind === 'survey') {
    if (activity.details?.surveyType === 'employee') {
      return {
        verb: 'Sending',
        subjectPreposition: 'on',
        extra: { type: 'recipient', noun: 'user' },
      };
    }
    // Candidate survey (default): sent straight to the candidate.
    return { verb: 'Sending', subjectPreposition: 'to', extra: null };
  }

  if (isExternalAssessment(kind)) {
    return {
      verb: 'Sending',
      subjectPreposition: 'to',
      extra: {
        type: 'using',
        label: getProviderName(kind),
        icon: getActivityKindIcon(kind),
      },
    };
  }

  // email, assessment (Kula), and any other "send to the candidate" kind.
  return { verb: 'Sending', subjectPreposition: 'to', extra: null };
};
